import { 
  getFirestore, 
  collection, 
  query, 
  where, 
  getDocs,
  getDoc, 
  orderBy,
  onSnapshot,
  doc,
  addDoc,
  serverTimestamp,
  updateDoc,
  setDoc,
  arrayUnion
} from "firebase/firestore";
import { firebaseApp } from "../../firebase"; // adjust path

const db = getFirestore(firebaseApp);

const getUserByRef = async (userRef) => {
  if (!userRef) return null;
  const userSnap = await getDoc(userRef);
  return userSnap.exists() ? userSnap : null;
};

export const getTop10Groups = async () => {
  const q = query(collection(db, "oneSG"));
  const snapshot = await getDocs(q);

  // Step 1: Load all groups
  let groups = await Promise.all(
    snapshot.docs.map(async (docSnap) => {
      const groupData = docSnap.data();
      const user = await getUserByRef(groupData.employerUID);
      return {
        id: docSnap.id,
        ref: docSnap.ref, // keep reference for event query
        ...groupData,
        employerUser: user,
      };
    })
  );

  // Step 2: Sort groups by memberSubscription length (descending) and pick top 10
  groups = groups
    .map((g) => ({
      ...g,
      pax: g.memberSubscription ? g.memberSubscription.length : 0,
    }))
    .sort((a, b) => b.pax - a.pax)
    .slice(0, 10);

  // Step 3 & 4: For each group, get calendar events within 14 days
  const today = new Date();
  const twoWeeksFromNow = new Date();
  twoWeeksFromNow.setDate(today.getDate() + 14);

  const enrichedGroups = await Promise.all(
    groups.map(async (group) => {
      const eventQ = query(
        collection(db, "calendarEvents"),
        where("oneSG", "==", group.ref) // filter by oneSG reference
      );
      const eventSnap = await getDocs(eventQ);

      let upcomingEvents = 0;
      eventSnap.forEach((docSnap) => {
        const eventData = docSnap.data();
        if (eventData.startDate) {
          const startDate = eventData.startDate.toDate
            ? eventData.startDate.toDate() // Firestore Timestamp
            : new Date(eventData.startDate); // fallback if stored as string

          if (startDate >= today && startDate <= twoWeeksFromNow) {
            upcomingEvents += 1;
          }
        }
      });
const bannerQ = query(
        collection(db, "oneSGMainNotificationBanner"),
        where("oneSGMainRef", "==", group.ref)
      );
      const bannerSnap = await getDocs(bannerQ);
      let image = 'https://firebasestorage.googleapis.com/v0/b/nextloop-31377.appspot.com/o/banner%2FSGLoop%20Banner%20S.png?alt=media&token=e70dc0bb-6ba3-41e8-ab48-e10bdead20fe';
      if (!bannerSnap.empty) {
        // Take the first banner found
        const bannerData = bannerSnap.docs[0].data();
        image = bannerData.imageURL || 'https://firebasestorage.googleapis.com/v0/b/nextloop-31377.appspot.com/o/banner%2FSGLoop%20Banner%20S.png?alt=media&token=e70dc0bb-6ba3-41e8-ab48-e10bdead20fe';
      }

      return {
        ...group,
        upcomingEvents,
        image,
      };
    })
  );

  

  return enrichedGroups;
};

export const getAllGroupsOrdered = async () => {
  const q = query(
    collection(db, "oneSG"),
    orderBy("order", "asc") // change to "desc" if you want reverse order
  );

  const snapshot = await getDocs(q);

  const groups = await Promise.all(
    snapshot.docs.map(async (docSnap) => {
      const groupData = docSnap.data();
      const user = await getUserByRef(groupData.employerUID);

      // --- Banner fetch logic ---
      const bannerQ = query(
        collection(db, "oneSGMainNotificationBanner"),
        where("oneSGMainRef", "==", docSnap.ref)
      );
      const bannerSnap = await getDocs(bannerQ);

      let image =
        "https://firebasestorage.googleapis.com/v0/b/nextloop-31377.appspot.com/o/banner%2FSGLoop%20Banner%20S.png?alt=media&token=e70dc0bb-6ba3-41e8-ab48-e10bdead20fe";

      if (!bannerSnap.empty) {
        const bannerData = bannerSnap.docs[0].data();
        image =
          bannerData.imageURL ||
          "https://firebasestorage.googleapis.com/v0/b/nextloop-31377.appspot.com/o/banner%2FSGLoop%20Banner%20S.png?alt=media&token=e70dc0bb-6ba3-41e8-ab48-e10bdead20fe";
      }

      return {
        id: docSnap.id,
        ref: docSnap.ref,
        ...groupData,
        employerUser: user,
        image,
      };
    })
  );

  return groups;
};

export const fetchAllSubGroups = async (selectedGroup) => {
  if (!selectedGroup?.ref) {
    console.warn("⚠️ No group reference provided to fetch sub-groups.");
    return [];
  }

  try {
    // Subcollection path: oneSG/{groupId}/subOneSG
    const subGroupCollectionRef = collection(selectedGroup.ref, "subOneSG");
    const snapshot = await getDocs(subGroupCollectionRef);

    const subGroups = snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ref: docSnap.ref,
      ...docSnap.data(),
    }));

    return subGroups;
  } catch (err) {
    console.error("Error fetching sub-groups:", err);
    return [];
  }
};

/**
 * Listen to all messages in a chat's subcollection
 * @param {string} chatId - Firestore document ID of the chat
 * @param {function} callback - Function that receives an array of messages in real-time
 * @returns {function} unsubscribe - Call this to stop listening
 */
export const listenToChatMessages = (chatId, callback) => {
  if (!chatId) {
    console.warn("⚠️ No chatId provided to listen to chat messages.");
    return () => {};
  }

  const messagesRef = collection(db, "oneSGChats", chatId, "OnesgChatMessage");
  const q = query(messagesRef, orderBy("timestamp", "asc"));

  const unsubscribe = onSnapshot(
    q,
    async (snapshot) => {
      try {
        const messages = await Promise.all(
          snapshot.docs.map(async (docSnap) => {
            const data = docSnap.data();

            let user = null;
            if (data.uidOfSender) {
              user = await getUserByRef(data.uidOfSender);
            }

            return {
              id: docSnap.id,
              ...data,
              sender: user ? { id: user.id, ...user.data() } : null,
            };
          })
        );

        callback(messages);
      } catch (err) {
        console.error("❌ Error resolving user data:", err);
      }
    },
    (error) => {
      console.error("❌ Error listening to chat messages:", error);
    }
  );

  return unsubscribe;
};

/**
 * Listen to all banner changes for a given chat reference
 * @param {object} chatRef - Firestore DocumentReference for the chat
 * @param {function} callback - Function that receives an array of banners in real-time
 * @returns {function} unsubscribe - Call this to stop listening
 */
export const listenToUpcomingEventsMainGroup = (mainGroupRef, callback) => {
  if (!mainGroupRef) {
    console.warn("⚠️ No valid mainGroupRef provided to listen to main banners.");
    return () => {};
  }

  const mainGroupDocRef = doc(db, "oneSG", mainGroupRef.id);

  const bannersRef = collection(db, "OneSGMainBanner");
  const q = query(bannersRef, where("oneSGMainChat", "==", mainGroupDocRef), where("status", "==", "Active"));

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      try {
        const banners = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        callback(banners);
      } catch (err) {
        console.error("❌ Error resolving banners:", err);
      }
    },
    (error) => {
      console.error("❌ Error listening to main banners:", error);
    }
  );

  return unsubscribe;
};

/**
 * Listen to all active sub chat banners for a given sub chat reference
 * @param {object} subChatRef - Firestore DocumentReference for the sub chat
 * @param {function} callback - Function that receives an array of active sub chat banners in real-time
 * @returns {function} unsubscribe - Call this to stop listening
 */
export const listenToUpcomingEventsSubGroup = (subChatRef, callback) => {
  if (!subChatRef) {
    console.warn("⚠️ No valid subChatRef provided to listen to sub chat banners.");
    return () => {};
  }
  const subChatDocRef = doc(
    db,
    "oneSG",
    subChatRef.ref.parent.parent.id, // main group id
    "subOneSG",
    subChatRef.id                     // sub group id
  );

  // Point to the OnesgSubChatBanner collection
  const subBannersRef = collection(db, "OnesgSubChatBanner");

  // Query: match by oneSGSubChat ref + only active
  const q = query(
    subBannersRef,
    where("oneSGSubChat", "==", subChatDocRef),
    where("status", "==", "Active")
  );

  // Real-time listener
  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      try {
        const events = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        callback(events);
      } catch (err) {
        console.error("❌ Error resolving sub chat banners:", err);
      }
    },
    (error) => {
      console.error("❌ Error listening to sub chat banners:", error);
    }
  );

  return unsubscribe;
}

/**
 * Send a new message into a subgroup chat
 * @param {string} chatId - Firestore document ID of the chat (from selectedSubGroup.oneSGchats.id)
 * @param {string} message - Text message to send
 * @param {object} userRef - Firestore DocumentReference of the user (from useAuth)
 */
export const sendMessage = async (chatId, message, userRef) => {
  if (!chatId || !message || !userRef) {
    console.warn("⚠️ Missing parameters for sendMessage");
    return;
  }

  try {
    // Step 1: Add message into chat subcollection
    const messagesRef = collection(db, "oneSGChats", chatId, "OnesgChatMessage");
    const newMsgRef = await addDoc(messagesRef, {
      message: message.trim(),
      timestamp: serverTimestamp(),
      uidOfSender: userRef,
    });

    // Step 2: Create a new document in innerOneSGChatMessage collection
    const innerRef = doc(db, "innerOneSGChatMessage", newMsgRef.id);
    await setDoc(innerRef, {
      OneSGChatMessageRef: newMsgRef
    });

    // Step 3: Update original message with reference to innerOneSGChatMessage
    await updateDoc(newMsgRef, {
      innerOneSGMessageRef: innerRef
    });

  } catch (err) {
    console.error("❌ Error sending message:", err);
  }
};

/**
 * Add current user into chatBefore array for an event
 * @param {string} source - "main" or "sub"
 * @param {string} eventId - Firestore document ID of the event
 * @param {object} userRef - Firestore DocumentReference of the current user
 */
export const signUpForEvent = async (source, eventId, userRef, mainGroupId, subGroupId) => {
  if (!eventId || !userRef || !source) {
    console.warn("⚠️ Missing params for signUpForEvent");
    return;
  }

  try {
    
    const collectionName = source?.subGroupName ? "OnesgSubChatBanner" : "OneSGMainBanner";
    const eventDocRef = doc(db, collectionName, eventId);
    console.log(collectionName);
    console.log(eventDocRef)
    // Add user to chatBefore array
    await updateDoc(eventDocRef, {
      chatBefore: arrayUnion(userRef),
    });

    const mainGroupRef = doc(db, "oneSG", mainGroupId);
    console.log(mainGroupRef)
    if (collectionName === "OnesgSubChatBanner") {
      if (!subGroupId) {
        console.warn("⚠️ subGroupId is missing for sub-group event");
        return;
      }
      const subGroupRef = doc(db, "oneSG", mainGroupId, "subOneSG", subGroupId);
      await addMemberToSubGroup(mainGroupRef, subGroupRef, userRef);
    } else {
      await addMemberToMainGroup(mainGroupRef, userRef);
    }

    console.log("✅ User signed up successfully!");
  } catch (err) {
    console.error("❌ Error signing up for event:", err);
  }
};

/**
 * Fetch all sub-groups that belong to the current user
 *
 * @param {Object} userRef - Firestore reference of the current user
 * @returns {Promise<Array>} - Array of subgroups that the user belongs to
 */
export const fetchMySubGroups = async (userRef) => {
  if (!userRef) {
    console.warn("⚠️ No userRef provided.");
    return [];
  }

  try {
    // 1️⃣ Fetch all groups in oneSG
    const oneSGRef = collection(db, "oneSG");
    const oneSGSnapshot = await getDocs(oneSGRef);

    const myGroups = oneSGSnapshot.docs
      .map((docSnap) => ({ id: docSnap.id, ref: docSnap.ref, ...docSnap.data() }))
      .filter((group) => {
        const admins = group.adminSubscription || [];
        const members = group.memberSubscription || [];
        // Check if userRef exists in either array
        const isAdmin = admins.some((ref) => ref?.id === userRef.id || ref?.path === userRef.path);
        const isMember = members.some((ref) => ref?.id === userRef.id || ref?.path === userRef.path);
        return isAdmin || isMember;
      });

    // 2️⃣ Fetch subgroups for each of myGroups
    const mySubGroups = [];

    for (const group of myGroups) {
      const subGroupCollectionRef = collection(group.ref, "subOneSG");
      const subGroupSnapshot = await getDocs(subGroupCollectionRef);

      subGroupSnapshot.docs.forEach((docSnap) => {
        const subGroup = { id: docSnap.id, ref: docSnap.ref, ...docSnap.data() };
        const admins = subGroup.adminMember || [];
        if (admins.some((ref) => ref?.id === userRef.id || ref?.path === userRef.path)) {
          mySubGroups.push(subGroup);
        }
      });
    }

    return mySubGroups;
  } catch (err) {
    console.error("❌ Error fetching my subgroups:", err);
    return [];
  }
};

/**
 * Fetch a single sub-group document
 *
 * @param {Object} userRef - Firestore reference of the current user
 * @param {string} subGroupPath - Full path to the sub-group document
 * @returns {Promise<Object|null>} - The sub-group data or null if not found
 */
export const fetchTargetedSubGroups = async (userRef, subGroupPath) => {
  if (!userRef) {
    console.warn("⚠️ No userRef provided.");
    return null;
  }
  if (!subGroupPath) {
    console.warn("⚠️ No Sub Group Path provided.");
    return null;
  }

  try {
    // Get reference to the document
    const subGroupDocRef = doc(db, subGroupPath);

    // Fetch the document
    const subGroupSnapshot = await getDoc(subGroupDocRef);

    if (subGroupSnapshot.exists()) {
      return { id: subGroupSnapshot.id, ...subGroupSnapshot.data() };
    } else {
      console.warn("⚠️ Sub-group document not found");
      return null;
    }
  } catch (err) {
    console.error("❌ Error fetching sub-group:", err);
    return null;
  }
};

/**
 * Create a membership document under Users > UID > membership
 * @param {object} userRefObj - Object containing Firestore doc reference for the user
 * @param {object} subGroupObj - Object containing Firestore doc reference for the subgroup
 * @param {object} membershipData - Data from the form
 * @returns {Promise<DocumentReference>} - Reference to the created membership doc
 */
export const updateSGloopUserMembership = async (userRefObj, subGroupObj, membershipData) => {
  console.log(!userRefObj)
  console.log(!subGroupObj)
  if (!userRefObj || !subGroupObj || !membershipData) {
    console.warn("⚠️ Missing parameters for updateSGloopUserMembership");
    return;
  }

  try {
    // Get actual Firestore DocumentReferences
    const userDocRef = doc(db, `/Users/${userRefObj.id}`);
    const subGroupDocRef = doc(db, subGroupObj.ref.path);

    // Reference to the membership subcollection under this user
    const membershipCollectionRef = collection(userDocRef, "membership");

    // Create a new document in membership collection
    const newMembershipDocRef = doc(membershipCollectionRef);

    await setDoc(newMembershipDocRef, {
      ...membershipData,
      subSGloopReference: subGroupDocRef, // Firestore reference to sub-group
      createdAt: serverTimestamp(),
    });

    console.log("✅ Membership successfully created!");
    return newMembershipDocRef;
  } catch (err) {
    console.error("❌ Error creating membership:", err);
    throw err;
  }
};

/**
 * Add a user to the main group's memberSubscription array
 * @param {object} groupRef - Firestore DocumentReference of the main group (/oneSG/{groupId})
 * @param {object} userRef - Firestore DocumentReference of the user
 */
export const addMemberToMainGroup = async (groupRef, userRef) => {
  if (!groupRef || !userRef) {
    console.warn("⚠️ Missing parameters for addMemberToMainGroup");
    return;
  }

  try {
    await updateDoc(groupRef, {
      memberSubscription: arrayUnion(userRef),
    });
    console.log(`✅ Added user ${userRef.id} to main group ${groupRef.id}`);
  } catch (err) {
    console.error("❌ Error adding user to main group:", err);
    throw err;
  }
};

/**
 * Add a user to a sub-group, and ensure they're also added to the main group
 * @param {object} mainGroupRef - Firestore DocumentReference of the main group (/oneSG/{groupId})
 * @param {object} subGroupRef - Firestore DocumentReference of the sub-group (/oneSG/{groupId}/subOneSG/{subGroupId})
 * @param {object} userRef - Firestore DocumentReference of the user
 */
export const addMemberToSubGroup = async (mainGroupRef, subGroupRef, userRef) => {
  if (!mainGroupRef || !subGroupRef || !userRef) {
    console.warn("⚠️ Missing parameters for addMemberToSubGroup");
    return;
  }

  try {
    // Step 1: Add user to main group first
    await addMemberToMainGroup(mainGroupRef, userRef);

    // Step 2: Add user to sub-group
    await updateDoc(subGroupRef, {
      subMembershipSubscription: arrayUnion(userRef),
    });

    console.log(
      `✅ Added user ${userRef.id} to sub-group ${subGroupRef.id} (also added to main group ${mainGroupRef.id})`
    );
  } catch (err) {
    console.error("❌ Error adding user to sub-group:", err);
    throw err;
  }
};